import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TextField, Button, Container, Typography, Box, Alert } from "@mui/material";
import { resetPassword } from "../services/authAPI";

function ResetPasswordForm() {
  // Initialize all state variables
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("At least 8 characters");
    }
    if (password.length > 20) {
      errors.push("No more than 20 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least one number");
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~]/.test(password)) {
      errors.push("At least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?/~)");
    }
    // Check for unallowed characters (single quote, double quote, backtick, whitespace)
    if (/['"`\s]/.test(password)) {
      errors.push("Cannot contain quotes, backticks, or spaces");
    }
    
    return errors;
  };

  useEffect(() => {
    // Verify token exists when component loads
    if (!token) {
      setError("Invalid or missing reset token in URL. Please check your reset link.");
      console.error("No token found in URL parameters");
    } 
  }, [token]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordErrors(validatePassword(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!token) {
      setError("Invalid or missing reset token.");
      return;
    }

    // Validate password
    const passwordValidationErrors = validatePassword(password);
    if (passwordValidationErrors.length > 0) {
      setError("Password does not meet requirements: " + passwordValidationErrors.join(", "));
      return;
    }

    setLoading(true);

    // Call the backend to update the password
    try {
      await resetPassword(token, password);
      setSuccess("Password reset successfully.");
      setTimeout(() => navigate("/login"), 3000); // Redirect to login after success
    } catch (err) {
      setError(err.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <Box mt={8}>
      <Container maxWidth="sm">
        <Typography variant="h4" component="h1" gutterBottom>
          Reset Password
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField
            label="New Password"
            name="password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={handlePasswordChange}
            required
            disabled={loading || !token}
            error={password.length > 0 && passwordErrors.length > 0}
            helperText={
              password.length > 0 && passwordErrors.length > 0
                ? passwordErrors.join(", ")
                : "8-20 characters with uppercase, lowercase, number, and special character (!@#$%^&*()_+-=[]{}|;:,.<>?/~). No quotes or spaces."
            }
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading || !token}
            error={
              confirmPassword.length > 0 &&
              password !== confirmPassword
            }
            helperText={
              confirmPassword.length > 0 &&
              password !== confirmPassword
                ? "Passwords do not match"
                : ""
            }
          />
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth
            disabled={
              loading ||
              !token ||
              passwordErrors.length > 0 ||
              password !== confirmPassword ||
              !password ||
              !confirmPassword
            }
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </form>
      </Container>
    </Box>
  );
}

export default ResetPasswordForm;